package dev.lingvo.site.components.editor

import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.LocaleProgressDto
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.PlaceholderProblemKind
import dev.lingvo.shared.Placeholders
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.QaCheck
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.shared.SourceSnapshotDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.TranslationOrigin
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.pluralCategoriesFor
import dev.lingvo.site.api.API_JSON
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.source.validSourceSnapshot
import dev.lingvo.site.ds.DiffKind
import dev.lingvo.site.ds.DiffPart
import dev.lingvo.site.ds.LProgressCounts
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.wordDiff
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.t

internal const val EDITOR_PAGE_SIZE = 50
internal const val EDITOR_LIST_OVERSCAN = 8
internal const val EDITOR_SEARCH_ID = "lingvo-editor-search"
internal const val EDITOR_TARGET_ID = "lingvo-editor-target"
internal const val KEY_ROW_ID_PREFIX = "lingvo-editor-key-row-"

internal enum class EditorStateFilter(
    val label: String,
    val apiState: TranslationState?,
) {
    ALL(tr("editor.editor_models.filter_all", "All"), null),
    UNTRANSLATED(tr("editor.editor_models.filter_untranslated", "Untranslated"), TranslationState.EMPTY),
    TRANSLATED(tr("editor.editor_models.filter_translated", "Translated"), TranslationState.TRANSLATED),
    APPROVED(tr("editor.editor_models.filter_approved", "Approved"), TranslationState.APPROVED),
    ;

    companion object {
        /**
         * The filter a `?state=` link names, or [ALL].
         *
         * Every counter in the product — the readiness row, the dashboard tile, the work queue —
         * wants to open the editor on what is left rather than on everything. Unparseable is [ALL]
         * rather than an error: a stale link should open the editor, which is the same rule
         * `?review=` already follows.
         */
        fun fromLink(value: String?): EditorStateFilter = entries
            .firstOrNull { it.name.equals(value?.trim(), ignoreCase = true) }
            ?: ALL
    }
}

internal data class EditorQuery(
    val search: String,
    val state: EditorStateFilter,
    val fileId: String,
    val tag: String,
    /**
     * Filters that arrive by link and have no control in the editor.
     *
     * Both are minted by the work queue, whose rows promise an exact set — "12 strings with changes
     * requested". They belong in the query key like every other clause, or switching filters would
     * serve one filter's rows from another's cache.
     */
    val review: ReviewStatus? = null,
    val stale: Boolean = false,
)

internal enum class EditorDraftShape { SCALAR, PLURAL, MIXED, UNKNOWN }

internal data class EditorDraft(
    val value: String = "",
    val pluralForms: Map<PluralCategory, String> = emptyMap(),
    val dirty: Boolean = false,
    val saving: Boolean = false,
    val provider: MtProvider? = null,
    val unsafeSuggestion: Boolean = false,
    /** Immutable claims captured with the text this draft started from. */
    val sourceSnapshot: SourceSnapshotDto? = null,
    val targetRevision: String? = null,
    val targetPresent: Boolean = false,
    val targetUpdatedAt: String? = null,
    val baseTarget: TranslationDto? = null,
    val sourceClaimCaptured: Boolean = false,
    val textShape: EditorDraftShape? = null,
) {
    fun hasContent(isPlural: Boolean): Boolean = if (isPlural) {
        pluralForms.values.any(String::isNotBlank)
    } else {
        value.isNotBlank()
    }
}

/** Legacy text shape is independent of unknown historical source provenance. */
internal fun EditorDraft.retainedShape(): EditorDraftShape {
    val scalar = value.isNotEmpty()
    val plural = pluralForms.values.any(String::isNotEmpty)
    if (scalar && plural) return EditorDraftShape.MIXED
    val declared = textShape ?: sourceSnapshot?.view?.isPlural?.let { if (it) EditorDraftShape.PLURAL else EditorDraftShape.SCALAR }
    if (declared == EditorDraftShape.SCALAR && plural || declared == EditorDraftShape.PLURAL && scalar) return EditorDraftShape.MIXED
    return declared ?: when {
        pluralForms.isNotEmpty() -> EditorDraftShape.PLURAL
        scalar -> EditorDraftShape.SCALAR
        else -> EditorDraftShape.UNKNOWN
    }
}

internal sealed interface EditorSuggestion {
    val id: String
    val value: String?
    val pluralForms: Map<PluralCategory, String>?
    val warning: String?

    data class TranslationMemory(
        override val id: String,
        override val value: String,
        val similarity: Double,
        val sourceKeyName: String,
        val matchedSource: String?,
        override val warning: String? = null,
    ) : EditorSuggestion {
        override val pluralForms: Map<PluralCategory, String>? = null
    }

    data class Machine(
        override val id: String,
        val provider: MtProvider,
        override val value: String?,
        override val pluralForms: Map<PluralCategory, String>?,
        val accepted: Boolean,
        override val warning: String? = null,
    ) : EditorSuggestion
}

internal data class InlineQaFinding(
    val severity: QaSeverity,
    val message: String,
    val fragment: String? = null,
    val pluralCategory: PluralCategory? = null,
)

internal data class FieldQaPresentation(
    val error: String?,
    val inlineFindings: List<InlineQaFinding>,
)

internal fun pluralQaForCategory(
    findings: List<InlineQaFinding>,
    category: PluralCategory,
): List<InlineQaFinding> = findings.filter { finding ->
    finding.pluralCategory == null || finding.pluralCategory == category
}

internal fun fieldQaPresentation(findings: List<InlineQaFinding>): FieldQaPresentation {
    val errorIndex = findings.indexOfFirst { it.severity == QaSeverity.ERROR }
    if (errorIndex < 0) return FieldQaPresentation(error = null, inlineFindings = findings)
    return FieldQaPresentation(
        error = findings[errorIndex].message,
        inlineFindings = findings.filterIndexed { index, _ -> index != errorIndex },
    )
}

internal fun initialDraft(row: KeyRow, locale: String): EditorDraft {
    val categories = pluralCategoriesFor(locale)
    val draft = if (row.key.isPlural) {
        EditorDraft(
            pluralForms = categories.associateWith { category ->
                row.target?.pluralForms?.get(category).orEmpty()
            },
        )
    } else {
        EditorDraft(value = row.target?.value.orEmpty())
    }
    return draft.copy(
        sourceSnapshot = row.validSourceSnapshot(),
        targetRevision = row.target?.revision,
        targetPresent = row.target != null,
        targetUpdatedAt = row.target?.updatedAt,
        baseTarget = row.target,
        sourceClaimCaptured = Session.serverFeatures.sourceAwareReview || row.sourceSnapshot != null,
        textShape = if (row.key.isPlural) EditorDraftShape.PLURAL else EditorDraftShape.SCALAR,
    )
}

internal fun EditorDraft.requestState(isPlural: Boolean, approve: Boolean): TranslationState = when {
    !hasContent(isPlural) -> TranslationState.EMPTY
    approve -> TranslationState.APPROVED
    else -> TranslationState.TRANSLATED
}

internal fun KeyRow.sourceText(): String = source.value
    ?: source.pluralForms?.get(PluralCategory.OTHER)
    ?: source.pluralForms?.values?.firstOrNull()
    ?: ""

internal fun KeyRow.sourceFor(category: PluralCategory): String =
    source.pluralForms?.get(category)
        ?: source.pluralForms?.get(PluralCategory.OTHER)
        ?: source.value.orEmpty()

internal fun inlineQa(
    row: KeyRow,
    draft: EditorDraft,
    locale: String,
): List<InlineQaFinding> {
    val findings = mutableListOf<InlineQaFinding>()
    val canonicalCandidate = locale == row.source.locale && locale == draft.sourceSnapshot?.view?.sourceLocale
    if (row.key.isPlural) {
        pluralCategoriesFor(locale).forEach { category ->
            val target = draft.pluralForms[category].orEmpty()
            if (target.isBlank()) {
                findings += InlineQaFinding(
                    severity = QaSeverity.ERROR,
                    message = trf(
                        "editor.editor_models.form_is_required_for_this_locale",
                        "{form} is required for this locale.",
                        "form" to category.displayName(),
                    ),
                    fragment = category.displayName(),
                    pluralCategory = category,
                )
            } else {
                findings += placeholderFindings(row.sourceFor(category), target, category, canonicalCandidate)
                row.key.maxLength?.let { maximum ->
                    if (target.length > maximum) {
                        findings += InlineQaFinding(
                            severity = QaSeverity.WARNING,
                            message = tPlural(
                                "editor.editor_models.form_is_over_the_limit",
                                target.length - maximum,
                                "{form} is {count} character over the limit.",
                                "{form} is {count} characters over the limit.",
                                "form" to category.displayName(),
                            ),
                            fragment = target,
                            pluralCategory = category,
                        )
                    }
                }
                if (target != target.trim()) {
                    findings += InlineQaFinding(
                        severity = QaSeverity.WARNING,
                        message = trf(
                            "editor.editor_models.form_has_leading_or_trailing_whitespace",
                            "{form} has leading or trailing whitespace.",
                            "form" to category.displayName(),
                        ),
                        fragment = target,
                        pluralCategory = category,
                    )
                }
            }
        }
    } else {
        val target = draft.value
        if (target.isBlank()) {
            findings += InlineQaFinding(
                severity = QaSeverity.ERROR,
                message = tr("editor.editor_models.translation_is_empty", "Translation is empty."),
            )
        } else {
            findings += placeholderFindings(row.sourceText(), target, canonicalCandidate = canonicalCandidate)
            row.key.maxLength?.let { maximum ->
                if (target.length > maximum) {
                    findings += InlineQaFinding(
                        severity = QaSeverity.WARNING,
                        message = tPlural(
                            "editor.editor_models.over_the_limit",
                            target.length - maximum,
                            "{count} character over the limit.",
                            "{count} characters over the limit.",
                        ),
                        fragment = target,
                    )
                }
            }
            if (target != target.trim()) {
                findings += InlineQaFinding(
                    severity = QaSeverity.WARNING,
                    message = tr("editor.editor_models.translation_has_leading_or_trailing_whitespace", "Translation has leading or trailing whitespace."),
                    fragment = target,
                )
            }
            if (!canonicalCandidate && target == row.sourceText() && target.isNotBlank()) {
                findings += InlineQaFinding(
                    severity = QaSeverity.WARNING,
                    message = tr("editor.editor_models.translation_is_the_same_as_the", "Translation is the same as the source."),
                    fragment = target,
                )
            }
        }
    }
    return findings.distinctBy { finding ->
        finding.severity to Triple(finding.message, finding.fragment, finding.pluralCategory)
    }
}

private fun placeholderFindings(
    source: String,
    target: String,
    pluralCategory: PluralCategory? = null,
    canonicalCandidate: Boolean = false,
): List<InlineQaFinding> =
    (if (canonicalCandidate) Placeholders.validate(null, target).filter { it.kind == PlaceholderProblemKind.ILLEGAL_FLAG }
        else Placeholders.validate(source, target)).map { problem ->
        // The sentences a translator reads next to their own text, so they are translated like any
        // other. `trf` rather than `tf`: this file holds no composables, and a QA finding is
        // computed while typing rather than during composition.
        val compactMessage = when (problem.kind) {
            PlaceholderProblemKind.MISSING ->
                trf("editor.editor_models.placeholder_missing", "Missing `{placeholder}`", "placeholder" to problem.placeholder)
            PlaceholderProblemKind.EXTRA ->
                trf("editor.editor_models.placeholder_extra", "Unexpected `{placeholder}`", "placeholder" to problem.placeholder)
            PlaceholderProblemKind.REORDERED ->
                tr("editor.editor_models.placeholder_reordered", "Unsafe placeholder order")
            PlaceholderProblemKind.TYPE_CHANGED ->
                trf(
                    "editor.editor_models.placeholder_type_changed",
                    "Placeholder type changed: `{placeholder}`",
                    "placeholder" to problem.placeholder,
                )
            PlaceholderProblemKind.ILLEGAL_FLAG ->
                trf(
                    "editor.editor_models.placeholder_illegal_flag",
                    "`{placeholder}` crashes when formatted",
                    "placeholder" to problem.placeholder,
                )
        }
        InlineQaFinding(
            severity = QaSeverity.ERROR,
            message = compactMessage,
            fragment = problem.placeholder,
            pluralCategory = pluralCategory,
        )
    }

internal fun mergedQa(
    live: List<InlineQaFinding>,
    remote: List<QaIssueDto>,
): List<InlineQaFinding> = (live + remote.map { issue ->
    InlineQaFinding(
        severity = issue.severity,
        message = issue.message,
        fragment = issue.details["placeholder"]?.toString()?.trim('"'),
    )
}).distinctBy { finding -> Triple(finding.severity, finding.message, finding.pluralCategory) }

internal fun suggestionWarning(row: KeyRow, value: String?): String? =
    Placeholders.validate(row.sourceText(), value.orEmpty()).firstOrNull()?.message

internal fun pluralSuggestionWarning(
    row: KeyRow,
    locale: String,
    forms: Map<PluralCategory, String>?,
): String? {
    val values = forms.orEmpty()
    val missing = pluralCategoriesFor(locale).firstOrNull { values[it].isNullOrBlank() }
    if (missing != null) {
        return trf(
            "editor.editor_models.missing_form",
            "Missing {form} form.",
            "form" to missing.displayName(),
        )
    }
    return pluralCategoriesFor(locale).firstNotNullOfOrNull { category ->
        Placeholders.validate(row.sourceFor(category), values[category]).firstOrNull()?.message
    }
}

/**
 * The plain-language example for a category, without repeating the category's own name.
 *
 * It used to read "one — 1 item", which was right when the name was not already on screen. It now
 * sits directly beside the heading that says "One", so carrying the word again produced
 * "One — one — 1 item". The example's job is the *number*, which is the part a translator cannot
 * infer from the CLDR name.
 */
internal fun pluralExample(category: PluralCategory, locale: String): String {
    val oneCategory = pluralCategoriesFor(locale).size == 1
    return when (category) {
        PluralCategory.ZERO -> "0 items"
        PluralCategory.ONE -> "1 item"
        PluralCategory.TWO -> "2 items"
        PluralCategory.FEW -> "2–4 items"
        PluralCategory.MANY -> "11–99 items"
        PluralCategory.OTHER -> if (oneCategory) "any number" else "5 items"
    }
}

internal fun PluralCategory.sampleCount(): Int = when (this) {
    PluralCategory.ZERO -> 0
    PluralCategory.ONE -> 1
    PluralCategory.TWO -> 2
    PluralCategory.FEW -> 3
    PluralCategory.MANY -> 11
    PluralCategory.OTHER -> 5
}

/**
 * The CLDR category as a word a reader can be shown, in their own language.
 *
 * It used to be `name.lowercase().replaceFirstChar { it.uppercase() }` — the enum constant with
 * English orthography applied to it. Every caller then passed that identifier into a *translated*
 * frame: `"{form} is required for this locale."` reached a German reader as "Other ist für diese
 * Sprache erforderlich.", a German sentence about an English identifier with no catalogue key
 * anywhere behind it. Six cases, one per category, so the frame and its subject are both
 * translatable and the plural editor's labels, placeholders and QA findings all move together.
 */
internal fun PluralCategory.displayName(): String = when (this) {
    PluralCategory.ZERO -> tr("editor.editor_models.plural_zero", "Zero")
    PluralCategory.ONE -> tr("editor.editor_models.plural_one", "One")
    PluralCategory.TWO -> tr("editor.editor_models.plural_two", "Two")
    PluralCategory.FEW -> tr("editor.editor_models.plural_few", "Few")
    PluralCategory.MANY -> tr("editor.editor_models.plural_many", "Many")
    PluralCategory.OTHER -> tr("editor.editor_models.plural_other", "Other")
}

/**
 * The form as it will read at [PluralCategory.sampleCount].
 *
 * A blank form returns blank rather than prose. It used to return "Add this form to see a live
 * preview.", which the caller prefixed with the sample count — "1 → Add this form to see a live
 * preview." — so the instruction arrived wearing the number it was meant to explain. The caller
 * draws no preview row at all for an empty form now, which is what "no preview" should look like.
 */
internal fun pluralPreview(value: String, category: PluralCategory): String {
    if (value.isBlank()) return ""
    val count = category.sampleCount().toString()
    val placeholders = Placeholders.extractAll(value)
    if (placeholders.isEmpty()) return value
    return buildString {
        var cursor = 0
        placeholders.forEach { placeholder ->
            append(value.substring(cursor, placeholder.start))
            val looksNumeric = placeholder.conversion in setOf("d", "i", "u") ||
                placeholder.name?.lowercase() in setOf("count", "number", "n") ||
                placeholder.index == 0
            append(if (looksNumeric) count else placeholder.raw)
            cursor = placeholder.end
        }
        append(value.substring(cursor))
    }
}

internal fun looksLikeCode(value: String): Boolean =
    value.contains('\n') ||
        // Every brace is escaped on purpose: Kotlin/JS compiles Regex with the `u` flag, and an
        // unescaped `}` is a syntax error there ("Lone quantifier brackets") even though the JVM
        // accepts it. This threw at runtime and blanked the whole editor.
        Regex("(%\\d*\\$?[a-zA-Z@]|\\{\\{?[A-Za-z0-9_.-]+\\}?\\}|<[^>]+>|\\\\[nrt])").containsMatchIn(value)

internal fun progressCounts(progress: LocaleProgressDto?): LProgressCounts {
    val total = progress?.total?.coerceAtLeast(0) ?: 0
    val approved = progress?.approved?.coerceIn(0, total) ?: 0
    val completed = progress?.translated?.coerceIn(approved, total) ?: 0
    return LProgressCounts(total = total, translated = completed - approved, approved = approved)
}

internal fun adjustProgress(
    progress: LProgressCounts,
    oldState: TranslationState,
    newState: TranslationState,
): LProgressCounts {
    if (oldState == newState) return progress
    var translated = progress.translated
    var approved = progress.approved
    when (oldState) {
        TranslationState.EMPTY -> Unit
        TranslationState.TRANSLATED -> translated--
        TranslationState.APPROVED -> approved--
    }
    when (newState) {
        TranslationState.EMPTY -> Unit
        TranslationState.TRANSLATED -> translated++
        TranslationState.APPROVED -> approved++
    }
    // **Clamped against `total` as well as zero, exactly as `progressCounts` does.**
    // `LProgressCounts.init` refuses `approved + translated > total`, and this runs optimistically
    // — after the row has been replaced and before `updateTranslation` is called. `total` comes
    // from the project DTO loaded once, while `rows` is refetched by `loadKeys`, so a `lingvo push`
    // that adds keys while the editor is open leaves the counter stale by construction. Saving one
    // of the new keys then threw out of the save coroutine: the translation was never sent and the
    // reader saw it as saved. A stale total degrades the counter; it must not lose the edit.
    val total = progress.total
    val clampedApproved = approved.coerceIn(0, total)
    return LProgressCounts(
        total = total,
        translated = translated.coerceIn(0, total - clampedApproved),
        approved = clampedApproved,
    )
}

internal fun highestSeverity(issues: List<QaIssueDto>): QaSeverity? = when {
    issues.any { it.severity == QaSeverity.ERROR } -> QaSeverity.ERROR
    issues.any { it.severity == QaSeverity.WARNING } -> QaSeverity.WARNING
    else -> null
}

/**
 * Machine output nobody has signed off.
 *
 * One definition, used by the key list's chip and the editor's badge, because two copies of this
 * rule is one that eventually disagrees — and the surface that disagrees is the one telling a
 * reviewer a string has been checked when it has not.
 *
 * **APPROVED is excluded rather than accommodated.** An approved machine translation has been
 * reviewed by definition; badging it would train people to ignore the badge on the ones that matter.
 */
internal fun isMachineUnreviewed(translation: TranslationDto?): Boolean =
    translation != null &&
        translation.origin == TranslationOrigin.MACHINE &&
        translation.state != TranslationState.APPROVED

/**
 * The name of a check as a person reads it, which is not the name of the enum.
 *
 * Every one of these is a heading on the QA page and a label on an exemption, so they are catalogue
 * strings. They were literals in a `when`, which is a position the extractor cannot reach — it wraps
 * literals at call sites, and a branch result is not one — so nine headings stayed English in every
 * locale while the page around them changed language.
 */
internal fun QaCheck.displayName(): String = when (this) {
    QaCheck.EMPTY -> tr("editor.editor_models.qa_empty", "Empty translations")
    QaCheck.PLACEHOLDER_MISMATCH -> tr("editor.editor_models.qa_placeholder_mismatch", "Placeholder mismatch")
    QaCheck.MISSING_PLURAL_FORM -> tr("editor.editor_models.qa_missing_plural_form", "Missing plural forms")
    QaCheck.LENGTH_EXCEEDED -> tr("editor.editor_models.qa_length_exceeded", "Length exceeded")
    QaCheck.LEADING_TRAILING_WHITESPACE ->
        tr("editor.editor_models.qa_leading_trailing_whitespace", "Leading or trailing whitespace")
    QaCheck.SAME_AS_SOURCE -> tr("editor.editor_models.qa_same_as_source", "Same as source")
    QaCheck.TAG_MISMATCH -> tr("editor.editor_models.qa_tag_mismatch", "Tag mismatch")
    QaCheck.GLOSSARY_VIOLATION -> tr("editor.editor_models.qa_glossary_violation", "Glossary violations")
    QaCheck.WRONG_QUOTE_STYLE -> tr("editor.editor_models.qa_wrong_quote_style", "Quote style")
}

/**
 * A server instant as it appears on a history card or a comment.
 *
 * **The zone marker is the point.** This used to slice the ISO string and then `removeSuffix("Z")`,
 * so every version and every comment in the editor printed a UTC wall-clock reading with nothing
 * saying so — a time that looks local, is not, and is wrong by the reader's own offset in the one
 * panel opened to work out who changed what and when. `formatUtc` is the design system's answer to
 * exactly this, and the audit log already renders the same shape through it beside the same kind of
 * author label. Malformed input still comes back as written rather than as a mangled slice.
 */
internal fun compactTimestamp(value: String): String = formatUtc(value, LTimeStyle.STAMP)

/**
 * Who did it, in words.
 *
 * [frozenLabel] is the name captured at write time and is preferred over everything except "You",
 * because it survives the account being closed. Without it a departed colleague's edits read as
 * "System" — `author_id` is `ON DELETE SET NULL` and null already means an automated write — and
 * everybody else read as six characters of a UUID, which is not a name.
 *
 * The UUID fallback remains for rows written before the label column existed. Those cannot be
 * recovered: the information was destroyed when the account closed.
 */
internal fun authorLabel(
    authorId: String?,
    currentUserId: String?,
    frozenLabel: String? = null,
): String = when {
    authorId != null && authorId == currentUserId -> tr("editor.editor_models.author_you", "You")
    !frozenLabel.isNullOrBlank() -> frozenLabel
    authorId == null -> tr("editor.editor_models.author_system", "System")
    else -> trf("editor.editor_models.author_contributor", "Contributor {id}", "id" to authorId.take(6))
}

internal fun historyDisplay(value: String?, forms: Map<PluralCategory, String>?): String = value
    ?: forms.orEmpty().entries.joinToString(" · ") { (category, form) ->
        // A category name beside its value. The separator is the message, so a locale that
        // punctuates a label differently can say so.
        trf(
            "editor.editor_models.history_form",
            "{category}: {value}",
            "category" to category.name.lowercase(),
            "value" to form.ifBlank { "—" },
        )
    }.ifBlank { "Cleared" }

/**
 * Above this many tokens on either side the alignment is abandoned for a whole-value replacement.
 *
 * The table is O(n × m). UI strings are cap-limited well below this, so the ceiling exists for the
 * paragraph somebody eventually pastes into a description field, not for ordinary work.
 */
/**
 * Split into comparable tokens, with every placeholder kept whole.
 *
 * Atomicity is the point. `%1$s` split into `%1` and `$s` is two tokens that happen to appear in
 * both versions, so the single most dangerous edit in the product — reordering two positional
 * placeholders, which corrupts arguments and crashes on Apple platforms — would align as unchanged.
 * `extractAll` is the same tokeniser the QA check uses, so the diff and the check cannot disagree
 * about where a placeholder starts.
 *
 * Whitespace runs are tokens too, so the rendered diff keeps the spacing of the original.
 */

internal data class PluralCategoryDiff(
    val category: PluralCategory,
    val parts: List<DiffPart>,
)

/**
 * A plural row diffed per category, drawing only the forms that moved.
 *
 * The history card used to flatten the whole map through `historyDisplay` first —
 * `one: … · few: … · many: …` — and word-diff the result as one sentence. Every category then
 * shared a token stream, so a Polish key with four forms reported an edit to `many` as changes
 * scattered across a line that also contained `one` and `few`, and the labels themselves became
 * diffable tokens. Which form actually changed, the only thing a reviewer opens this panel to
 * learn, had to be worked out by eye.
 *
 * Categories are walked in declaration order rather than map order so two saves of the same key
 * cannot list their forms differently. A category whose parts are all `UNCHANGED` is dropped: an
 * approval that rewrote nothing has nothing to draw.
 */
internal fun pluralDiff(
    previous: Map<PluralCategory, String>?,
    current: Map<PluralCategory, String>?,
): List<PluralCategoryDiff> = PluralCategory.entries
    .filter { it in previous.orEmpty() || it in current.orEmpty() }
    .mapNotNull { category ->
        val parts = wordDiff(
            previous?.get(category).orEmpty(),
            current?.get(category).orEmpty(),
        )
        if (parts.none { it.kind != DiffKind.UNCHANGED }) null
        else PluralCategoryDiff(category, parts)
    }

/**
 * Two versions of one translation, waiting for a person to choose.
 *
 * Both are kept until someone clicks. The local draft stays in `drafts`, so a refresh does not lose
 * what was typed — the failure this replaces is the loser of a race discovering it in the History
 * tab, if ever.
 */
internal data class EditorConflict(
    val keyId: String,
    /** What this browser had typed. */
    val mine: EditorDraft,
    /** What is actually stored, from the 409. */
    val theirs: TranslationDto,
)

/**
 * The translation carried by a `409 translation_conflict`.
 *
 * Null when the payload is missing or unreadable, in which case the editor falls back to telling
 * someone to reload rather than showing an empty comparison.
 */
internal fun ApiException.currentTranslation(): TranslationDto? =
    details["current"]?.let { element ->
        runCatching { API_JSON.decodeFromJsonElement(TranslationDto.serializer(), element) }.getOrNull()
    }
